// pages/LoginPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Avatar,
  IconButton,
  InputAdornment,
  Container,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  School,
  ArrowBack,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

// Academic Theme
const academicTheme = createTheme({
  palette: {
    primary: {
      main: "#1565C0",
    },
    secondary: {
      main: "#FF8F00",
    },
    background: {
      default: "#F8F9FA",
      paper: "#FFFFFF",
    },
  },
});

const LoginPage = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // ถ้า login อยู่แล้ว ให้ redirect ไปหน้า dashboard
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await login(form.username, form.password);
      navigate("/dashboard"); // เมื่อล็อคอินสำเร็จไปหน้า Dashboard
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <ThemeProvider theme={academicTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2,
        }}
      >
        {/* Back Button */}
        <IconButton
          onClick={() => navigate("/academic")}
          sx={{
            position: "absolute",
            top: 20,
            left: 20,
            color: "white",
            backgroundColor: "rgba(255,255,255,0.1)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.2)",
            },
          }}
        >
          <ArrowBack />
        </IconButton>

        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 4,
              backgroundColor: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
            }}
          >
            {/* โลโก้ */}
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: "auto",
                mb: 2,
                backgroundColor: "#1565C0",
              }}
            >
              <School fontSize="large" />
            </Avatar>

            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{ color: "#1565C0" }}
            >
              เข้าสู่ระบบ
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, color: "#666" }}>
              มหาวิทยาลัยเทคโนโลยีนวัตกรรม
            </Typography>

            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                label="ชื่อผู้ใช้"
                variant="outlined"
                value={form.username}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const sanitized = rawValue.replace(/\s/g, "").toLowerCase();
                  setForm({ ...form, username: sanitized });
                }}
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
              <TextField
                label="รหัสผ่าน"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: "none",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  backgroundColor: "#1565C0",
                  "&:hover": {
                    backgroundColor: "#0D47A1",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                เข้าสู่ระบบ
              </Button>
            </form>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default LoginPage;
